import { CheckCircleFilled, CloseCircleFilled } from '@ant-design/icons'
import React from 'react'
import styled, { keyframes } from 'styled-components'

import { STATUS_FILTER_OPTIONS } from 'features/projects/constants'
import { ProjectStatuses } from 'types/enums'

type Variant = 'success' | 'failure' | 'active'

// Hash map: only terminal states are enumerated; everything else falls through
// to the 'active' (in-progress) default.
const STATUS_VARIANTS: Partial<Record<ProjectStatuses, Variant>> = {
  [ProjectStatuses.COMPLETE]: 'success',
  [ProjectStatuses.ERROR]: 'failure',
}

const STATUS_LABELS: Record<string, string> = Object.fromEntries(
  STATUS_FILTER_OPTIONS.map((o) => [o.value, o.text]),
)

// GitHub primer palette colors
const COLOR_SUCCESS = '#1a7f37'
const COLOR_FAILURE = '#cf222e'
const COLOR_ACTIVE = '#9a6700'

const spin = keyframes`
  to { transform: rotate(360deg); }
`

const SuccessIcon = styled(CheckCircleFilled)`
  color: ${COLOR_SUCCESS};
  font-size: 14px;
  flex-shrink: 0;
`

const FailureIcon = styled(CloseCircleFilled)`
  color: ${COLOR_FAILURE};
  font-size: 14px;
  flex-shrink: 0;
`

const SpinningRing = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  flex-shrink: 0;
  animation: ${spin} 1.4s linear infinite;
  color: ${COLOR_ACTIVE};

  svg {
    display: block;
  }
`

const ActiveIcon = (): React.JSX.Element => (
  <SpinningRing aria-hidden="true">
    <svg
      width="14"
      height="14"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* 3/4 arc from 12 o'clock sweeping clockwise to 9 o'clock — gap makes rotation visible */}
      <path
        d="M8 2 A6 6 0 1 1 2 8"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
      />
      <circle cx="8" cy="8" r="2" fill="currentColor" />
    </svg>
  </SpinningRing>
)

const ICON_MAP: Record<Variant, React.ReactElement> = {
  success: <SuccessIcon />,
  failure: <FailureIcon />,
  active: <ActiveIcon />,
}

const Wrapper = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
`

const Label = styled.span`
  font-size: 13px;
`

export type LiveStatusDisplayProps = { state: string }

export const LiveStatusDisplay = ({
  state,
}: LiveStatusDisplayProps): React.JSX.Element => {
  const variant = STATUS_VARIANTS[state as ProjectStatuses] ?? 'active'
  const label = STATUS_LABELS[state] ?? state
  return (
    <Wrapper>
      {ICON_MAP[variant]}
      <Label>{label}</Label>
    </Wrapper>
  )
}
